// Product row of Product structures (2026-11-01): the last 5 products opened in this browser as
// pills, then ⋯ for a searchable list of every product. Recents are a per-browser convenience
// (localStorage, wrapped — the page works without it).
import React, { useEffect, useMemo, useRef, useState } from 'react';

const KEY = 'ps.recentProducts';
const MAX = 5;

export function readRecent() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]').filter((x) => typeof x === 'string'); } catch { return []; }
}

export function rememberProduct(node) {
  if (!node) return;
  try { localStorage.setItem(KEY, JSON.stringify([node, ...readRecent().filter((x) => x !== node)].slice(0, 20))); } catch { /* ignore */ }
}

const label = (r) => `${r.key} · ${r.name || '(not in the filter)'}`;

export default function PsProductPicker({ roots, root, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);

  useEffect(() => { rememberProduct(root); }, [root]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  // Recents that still exist, current one always shown; topped up from the list so 5 show.
  const pills = useMemo(() => {
    const byNode = new Map(roots.map((r) => [r.node, r]));
    const order = [root, ...readRecent(), ...roots.map((r) => r.node)];
    const out = [];
    for (const n of order) {
      if (out.length >= MAX) break;
      if (byNode.has(n) && !out.some((r) => r.node === n)) out.push(byNode.get(n));
    }
    return out;
  }, [roots, root]);

  const all = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return [...roots]
      .sort((a, b) => label(a).localeCompare(label(b)))
      .filter((r) => !needle || label(r).toLowerCase().includes(needle));
  }, [roots, q]);

  const pick = (n) => { onPick(n); setOpen(false); setQ(''); };
  const more = roots.length - pills.length;

  return (
    <div className="ps-pills ps-pp" ref={box}>
      {pills.map((r) => (
        <button key={r.node} type="button" className={'ps-pill' + (r.node === root ? ' ps-pill--on' : '')} onClick={() => pick(r.node)}>
          {label(r)}
        </button>
      ))}
      {roots.length > 1 && (
        <button type="button" className={'ps-pill ps-pp-more' + (open ? ' ps-pp-more--open' : '')} aria-haspopup="listbox" aria-expanded={open}
          title="All products" onClick={() => setOpen((o) => !o)}>
          ⋯{more > 0 && <span className="ps-pp-n">+{more}</span>}
        </button>
      )}
      {open && (
        <div className="ps-bp-pop ps-pp-pop" role="listbox">
          <input className="ps-bp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${roots.length} products…`} autoFocus />
          <div className="ps-bp-list">
            {all.map((r) => (
              <button key={r.node} type="button" role="option" aria-selected={r.node === root}
                className={'ps-bp-row' + (r.node === root ? ' ps-bp-row--on' : '')} onClick={() => pick(r.node)}>
                <span className="ps-bp-name"><b>{r.key}</b><small>{r.name || '(not in the filter)'}</small></span>
                {readRecent().includes(r.node) && r.node !== root && <span className="ps-pp-recent">Recent</span>}
                {r.node === root && <span className="ps-bp-tick">✓</span>}
              </button>
            ))}
            {!all.length && <div className="ps-bp-empty">No product matches “{q}”.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
